import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';
import { PDFDocument } from 'pdf-lib';

/**
 * POST /api/documents/merge-pdf
 *
 * Body: { document_ids: string[] }
 *
 * Merges multiple PDF documents into a single PDF.
 * Creates a new document record with the merged file.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const supabaseAdmin = getSupabaseAdminClient();

  try {
    const { document_ids } = req.body;

    if (!document_ids || !Array.isArray(document_ids) || document_ids.length < 2) {
      return res.status(400).json({
        success: false,
        error: 'Se requieren al menos 2 document_ids para unir.',
      });
    }

    // Fetch all documents
    const { data: docs, error: docsError } = await supabaseAdmin
      .from('documentos')
      .select('id, empresa_id, cliente_id, nombre, url_archivo, tipo')
      .in('id', document_ids);

    if (docsError || !docs || docs.length < 2) {
      return res.status(404).json({
        success: false,
        error: 'No se encontraron suficientes documentos.',
      });
    }

    // Verify all are PDFs
    const nonPdf = docs.find(d => !d.nombre.toLowerCase().endsWith('.pdf'));
    if (nonPdf) {
      return res.status(400).json({
        success: false,
        error: `El documento "${nonPdf.nombre}" no es un PDF. Solo se pueden unir documentos PDF.`,
      });
    }

    // Create merged PDF
    const mergedPdf = await PDFDocument.create();

    for (const doc of docs) {
      const storagePath = doc.url_archivo.replace(
        /^https:\/\/[^/]+\/storage\/v1\/object\/public\/documentos\//,
        ''
      );

      if (!storagePath || storagePath === doc.url_archivo) {
        return res.status(500).json({
          success: false,
          error: `No se pudo determinar la ruta de "${doc.nombre}".`,
        });
      }

      const { data: fileData, error: fileError } = await supabaseAdmin.storage
        .from('documentos')
        .download(storagePath);

      if (fileError || !fileData) {
        return res.status(500).json({
          success: false,
          error: `Error descargando "${doc.nombre}".`,
        });
      }

      const pdfBytes = new Uint8Array(await fileData.arrayBuffer());

      try {
        const sourcePdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
        copiedPages.forEach(page => mergedPdf.addPage(page));
      } catch {
        return res.status(500).json({
          success: false,
          error: `Error procesando "${doc.nombre}". Puede estar corrupto o protegido.`,
        });
      }
    }

    const mergedPdfBytes = await mergedPdf.save();
    const mergedName = `union_${docs.length}_docs_${Date.now()}.pdf`;
    const firstDoc = docs[0];

    // Upload merged PDF
    const uploadPath = `${firstDoc.empresa_id || 'sin_empresa'}/${mergedName}`;
    const { error: uploadError } = await supabaseAdmin.storage
      .from('documentos')
      .upload(uploadPath, Buffer.from(mergedPdfBytes), {
        contentType: 'application/pdf',
        upsert: false,
      });

    if (uploadError) {
      return res.status(500).json({
        success: false,
        error: `Error subiendo el PDF unido: ${uploadError.message}`,
      });
    }

    // Get public URL
    const { data: urlData } = supabaseAdmin.storage
      .from('documentos')
      .getPublicUrl(uploadPath);

    // Create new document record
    const { data: newDoc, error: insertError } = await supabaseAdmin
      .from('documentos')
      .insert({
        nombre: mergedName,
        url_archivo: urlData.publicUrl,
        tipo: firstDoc.tipo || 'factura_proveedor',
        empresa_id: firstDoc.empresa_id,
        cliente_id: firstDoc.cliente_id,
        estado: 'pendiente',
      })
      .select()
      .single();

    if (insertError) {
      return res.status(500).json({
        success: false,
        error: `Error creando registro: ${insertError.message}`,
      });
    }

    return res.status(200).json({
      success: true,
      document: newDoc,
      pages_total: mergedPdf.getPageCount(),
      documents_merged: docs.length,
    });
  } catch (error: any) {
    console.error('[merge-pdf] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al unir los PDFs.',
    });
  }
}

export default withAuth(handler, ['admin', 'asesor']);
